import { useMutation, useQuery } from "@apollo/client";
import { useAuth } from "@frontegg/react";
import { GET_USER } from "../graphql/user";
import {
  GET_COMPANY,
  GET_COMPANY_CUSTOM_COLOR_SETTINGS,
  GET_INVOICE_NUMBER_SETTINGS,
  UPDATE_COMPANY_CUSTOM_COLOR_SETTINGS,
  UPDATE_COMPANY,
  GET_MILEAGE_REIMBURSEMENT,
  UPDATE_MILEAGE_REIMBURSEMENT,
  GET_DEFAULT_MILEAGE_RATES,
  UPDATE_INVOICE_NUMBER_SETTINGS,
} from "../graphql/company";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAttachmentAndAssetHandling } from "./useAttachmentAndAssetHandling";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { CompanyLogoSubparentType } from "enums/CompanyLogoSubparentType";
import httpClient from "utils/httpClient";
import { MileageReimbursementType } from "enums/MileageReimbursementType";
import generalSettings from "validations/generalSettings";
import { ThemeColors } from "utils/themeContextHelper";
import { toFileList } from "utils/receipts/receiptUpload";
import { currentLogoOfKind, logosOfKind } from "utils/branding/companyLogos";
import { useBrandingContext } from "context/BrandingContext";
import { InvoicePaymentTerms } from "enums/InvoicePaymentTerms";
import { InvoiceDiscountType } from "enums/InvoiceDiscountType";

/** The company stores an unset field as null; the form holds it as "". */
const isBlank = (value: unknown) =>
  value === "" || value === null || value === undefined;

interface ChangedFieldsArgs {
  values: Record<string, unknown>;
  stored: Record<string, unknown>;
  fields: string[];
  paymentEmailDomain: string;
}

/**
 * The company update payload: the fields whose value actually differs from what
 * the company holds.
 *
 * Two traps it exists to avoid. The form keeps paymentEmail as its prefix and
 * the domain separately, so comparing or sending the raw value drops the domain.
 * And a field the form does not represent comes back null while the form holds
 * "", which is not a change; sending it anyway is rejected outright by the
 * enum-typed ones, businessIncorporationState first.
 */
export const changedCompanyFields = ({
  values,
  stored,
  fields,
  paymentEmailDomain,
}: ChangedFieldsArgs): Record<string, unknown> =>
  fields.reduce((acc: Record<string, unknown>, key) => {
    const outgoing =
      key === "paymentEmail" && values[key]
        ? `${values[key]}${paymentEmailDomain}`
        : values[key];
    const held = stored[key];

    if (isBlank(outgoing) && isBlank(held)) return acc;
    if (outgoing !== held) acc[key] = outgoing;

    return acc;
  }, {});

export interface CompanyProfileTabProps {
  businessLegalName: string;
  description: string;
  businessDBA: string;
  businessCardName: string;
  businessEIN: string;
  businessStructure: string;
  businessIncorporationState: string;
  businessEmail: string;
  phoneNumber: string;
  websiteURL: string;
  paymentEmail: string;
  annualRevenue: number | "";
  [key: string]: string | number | boolean | null;
}

export interface CompanyInvoiceSettings {
  format: string;
  incrementLength: number | null;
  prefix: string;
  suffix: string;
  defaultPaymentTerms: InvoicePaymentTerms;
  defaultTax: number;
  defaultTaxType: InvoiceDiscountType;
}

export interface CompanyLogos {
  letterheadLogoAssetUrl: string;
  lightLetterheadLogoAssetUrl: string;
  iconLogoAssetUrl: string;
  cardImageToDisplayUrl: string;
}

export interface GeneralSettingsTabProps {
  mileageReimbursement: {
    type: string;
    rate: number | null;
  };
}

const useCompanySettings = ({
  initialValues,
  formikValidation,
  onSaveError,
}: {
  initialValues?: CompanyProfileTabProps;
  formikValidation?: any;
  onSaveError?: () => void;
} = {}) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { refetchBranding, updateBrandColors } = useBrandingContext();

  /////////////////////////////////////////
  //                                     //
  //           Company Profile           //
  //                                     //
  /////////////////////////////////////////

  const {
    data: userData,
    loading: loadingUser,
    refetch: refetchUser,
  } = useQuery(GET_USER, {
    variables: { userId: user?.id },
    skip: Boolean(!user?.id),
    fetchPolicy: "network-only",
  });
  const companyId = userData?.user?.company?.id;

  const {
    data: companyInfo,
    refetch: refetchCompanyInfo,
    loading: loadingCompanyInfo,
  } = useQuery(GET_COMPANY, {
    variables: { id: companyId },
    skip: Boolean(!companyId),
    fetchPolicy: "network-only",
  });

  const [updateCompany, { loading: updatingCompany }] =
    useMutation(UPDATE_COMPANY);

  const companyFields = [
    "businessLegalName",
    "businessDBA",
    "businessEIN",
    "businessStructure",
    "businessIncorporationState",
    "businessCardName",
    "description",
    "businessEmail",
    "phoneNumber",
    "websiteURL",
    "paymentEmail",
    "annualRevenue",
  ];

  const formikCompanyProfile = useFormik<CompanyProfileTabProps>({
    initialValues: {
      businessLegalName: "",
      description: "",
      businessDBA: "",
      businessCardName: "",
      businessEIN: "",
      businessEmail: "",
      businessStructure: "",
      businessIncorporationState: "",
      phoneNumber: "",
      websiteURL: "",
      paymentEmail: "",
      annualRevenue: "",
      ...(initialValues || {}),
    },
    validationSchema: formikValidation || undefined,
    onSubmit: async (values, formikHelpers) => {
      if (!formikCompanyProfile.dirty) return;
      try {
        const updatedCompanyFields = changedCompanyFields({
          values,
          stored: companyInfo.company,
          fields: companyFields,
          paymentEmailDomain,
        });

        if (Object.keys(updatedCompanyFields).length > 0) {
          await updateCompany({
            variables: { id: companyId, input: updatedCompanyFields },
          });
          refetchCompanyInfo();
        }

        toast.success(t("Company updated successfully"));
      } catch (error: any) {
        if (typeof onSaveError === "function") {
          onSaveError();
        }
        toast.error(t(error.message || "Error updating company"));
      }
    },
  });

  // Helper to split paymentEmail into prefix and domain
  const [paymentEmailDomain, setPaymentEmailDomain] = useState("");
  const updateFormikValues = (data: any, fields: string[]) => {
    return fields.reduce(
      (acc: Record<string, string>, field) => {
        // Blank included: skipping it here would leave the previous company's
        // domain in state, to be appended to whatever prefix is typed next.
        if (field === "paymentEmail") {
          const [prefix, domain] = String(data[field] ?? "").split("@");
          acc[field] = prefix || "";
          setPaymentEmailDomain(domain ? "@" + domain : "");
        } else {
          acc[field] = data[field] || "";
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  };

  useEffect(() => {
    if (companyInfo?.company?.id) {
      const updatedCompanySettings = updateFormikValues(
        companyInfo?.company || {},
        companyFields,
      );

      // reset form so we can reset dirty to check for changes
      formikCompanyProfile.resetForm({
        values: {
          ...formikCompanyProfile.values,
          ...updatedCompanySettings,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo]);

  // The schema can change under a mounted form: the payments entitlement
  // resolves after the first render, and it decides whether the payment email
  // is required. isValid is left over from the previous schema until something
  // triggers validation, so re-run it here. Only once the form is dirty, or a
  // pristine form would surface errors the reader has not earned yet.
  useEffect(() => {
    if (formikCompanyProfile.dirty) {
      formikCompanyProfile.validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikValidation]);

  /////////////////////////////////////////
  //                                     //
  //          Invoice Settings           //
  //                                     //
  /////////////////////////////////////////

  const {
    data: invoiceSettingsData,
    loading: loadingInvoiceSettingsData,
    refetch: refetchInvoiceSettings,
  } = useQuery(GET_INVOICE_NUMBER_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const getInvoiceInitialValues = (data?: any): CompanyInvoiceSettings => ({
    format: data?.getInvoiceNumberSettings?.format || "",
    incrementLength: data?.getInvoiceNumberSettings?.incrementLength ?? null,
    prefix: data?.getInvoiceNumberSettings?.prefix || "",
    suffix: data?.getInvoiceNumberSettings?.suffix || "",
    defaultPaymentTerms:
      data?.getInvoiceNumberSettings?.defaultPaymentTerms ||
      InvoicePaymentTerms.IMMEDIATELY,
    defaultTax: data?.getInvoiceNumberSettings?.defaultTax || 0,
    defaultTaxType:
      data?.getInvoiceNumberSettings?.defaultTaxType ||
      InvoiceDiscountType.PERCENTAGE,
  });

  const [updateCompanyInvoiceSettings, { loading: updatingInvoiceSettings }] =
    useMutation(UPDATE_INVOICE_NUMBER_SETTINGS);

  const formikCompanyInvoiceSettings = useFormik<CompanyInvoiceSettings>({
    initialValues: getInvoiceInitialValues(invoiceSettingsData),
    validationSchema: undefined,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await updateCompanyInvoiceSettings({ variables: { input: values } });
        refetchInvoiceSettings();
        toast.success(t("Invoice setting updated successfully"));
      } catch (error: any) {
        toast.error(t("Error updating invoice setting"));
      }
    },
  });

  /////////////////////////////////////////
  //                                     //
  //              Branding               //
  //                                     //
  /////////////////////////////////////////

  const {
    handleAddAttachments,
    handleRemoveAttachments,
    attachments,
    isUploadingAttachments,
  } = useAttachmentAndAssetHandling({
    parentDetails: {
      parentId: companyId,
      parentType: ATTACHMENT_PARENT_TYPE.COMPANY_LOGO,
    },
  });

  const logoUrl = (kind: CompanyLogoSubparentType) =>
    currentLogoOfKind(attachments, kind)?.assetUrl ?? "";

  const logos: CompanyLogos = {
    letterheadLogoAssetUrl: logoUrl(CompanyLogoSubparentType.LETTERHEAD_LOGO),
    lightLetterheadLogoAssetUrl: logoUrl(
      CompanyLogoSubparentType.LIGHT_LETTERHEAD_LOGO,
    ),
    iconLogoAssetUrl: logoUrl(CompanyLogoSubparentType.ICON_LOGO),
    cardImageToDisplayUrl: logoUrl(CompanyLogoSubparentType.CARD_IMAGE),
  };

  // Takes the File rather than the change event so a caller can hand over a
  // file it already holds. `toFileList` rebuilds the FileList that
  // `handleAddAttachments` branches on, detached from any live input.
  const uploadLogo = async (
    file: File,
    subParentType: CompanyLogoSubparentType,
  ): Promise<boolean> => {
    const isSvg =
      file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");
    if (!isSvg) {
      toast.error(t("Only SVG files are allowed for company branding"));
      return false;
    }

    const existingAttachment = currentLogoOfKind(attachments, subParentType);

    const uploaded = await handleAddAttachments(toFileList([file]), {
      parentId: companyId,
      parentType: ATTACHMENT_PARENT_TYPE.COMPANY_LOGO,
      subParentId: subParentType,
      id: existingAttachment?.id,
    });

    refetchBranding();
    return uploaded;
  };

  const handleRemoveLogo = async (subParentType: CompanyLogoSubparentType) => {
    // Every copy goes, so removing a logo never reveals an older duplicate.
    for (const logo of logosOfKind(attachments, subParentType)) {
      await handleRemoveAttachments(logo.id);
    }
    refetchBranding();
  };

  /////////////////////////////////////////
  //                                     //
  //               COLORS                //
  //                                     //
  /////////////////////////////////////////

  const {
    data: customColorData,
    loading: loadingCustomColors,
    refetch: refetchColors,
  } = useQuery(GET_COMPANY_CUSTOM_COLOR_SETTINGS, {
    fetchPolicy: "network-only",
  });

  const getCustomColors = (data: any): ThemeColors => ({
    primary: data?.getCompanyCustomColorSettings?.primary || "",
    secondary: data?.getCompanyCustomColorSettings?.secondary || "",
    navBackground: data?.getCompanyCustomColorSettings?.accent || "",
    tertiary: data?.getCompanyCustomColorSettings?.tertiary || "",
    success: data?.getCompanyCustomColorSettings?.success || "",
    warning: data?.getCompanyCustomColorSettings?.warning || "",
    danger: data?.getCompanyCustomColorSettings?.danger || "",
    info: data?.getCompanyCustomColorSettings?.info || "",
    chartStrategy: data?.getCompanyCustomColorSettings?.chartStrategy || "",
    chartLeadWithSecondary:
      data?.getCompanyCustomColorSettings?.chartLeadWithSecondary || "",
    chartThirdColor: data?.getCompanyCustomColorSettings?.chartThirdColor || "",
    paymentCardTextMode: data?.getCompanyCustomColorSettings?.cardText || "",
    paymentCardArt: data?.getCompanyCustomColorSettings?.cardArt || "",
    paymentCardBackground:
      data?.getCompanyCustomColorSettings?.cardBackground || "",
    paymentCardFace: data?.getCompanyCustomColorSettings?.cardFace || "",
  });

  const customColors = getCustomColors(customColorData);

  const [updateCompanyCustomColorSettings, { loading: updatingCustomColors }] =
    useMutation(UPDATE_COMPANY_CUSTOM_COLOR_SETTINGS);

  const handleUpdateCustomColors = async (
    colors: Partial<ThemeColors>,
  ): Promise<boolean> => {
    try {
      await updateCompanyCustomColorSettings({
        variables: {
          input: {
            primary: colors.primary,
            secondary: colors.secondary,
            accent: colors.navBackground,
            tertiary: colors.tertiary,
            success: colors.success,
            warning: colors.warning,
            danger: colors.danger,
            info: colors.info,
            chartStrategy: colors.chartStrategy,
            chartLeadWithSecondary: colors.chartLeadWithSecondary,
            chartThirdColor: colors.chartThirdColor,
            cardText: colors.paymentCardTextMode,
            cardArt: colors.paymentCardArt,
            cardBackground: colors.paymentCardBackground,
            cardFace: colors.paymentCardFace,
          },
        },
      });
      updateBrandColors(colors);
      refetchColors();
      return true;
    } catch (error) {
      toast.error(t("ErrorUpdatingColors"));
      return false;
    }
  };

  /////////////////////////////////////////
  //                                     //
  //               General               //
  //                                     //
  /////////////////////////////////////////

  const {
    data: mileageData,
    refetch: refetchMileageData,
    loading: loadingMileage,
  } = useQuery(GET_MILEAGE_REIMBURSEMENT, {
    fetchPolicy: "network-only",
  });

  const { data: defaultMileageData, loading: loadingDefaultMileage } = useQuery(
    GET_DEFAULT_MILEAGE_RATES,
    {
      fetchPolicy: "network-only",
    },
  );

  const [updateMileageSetting, { loading: updatingMileage }] = useMutation(
    UPDATE_MILEAGE_REIMBURSEMENT,
  );

  const formikGeneralSettings = useFormik<GeneralSettingsTabProps>({
    initialValues: {
      mileageReimbursement: {
        type: "",
        rate: null,
      },
    },
    validationSchema: generalSettings,
    onSubmit: async (values) => {
      try {
        const { type, rate } = values.mileageReimbursement;
        const input = {
          type,
          rate: type === MileageReimbursementType.CUSTOM ? rate : null,
        };

        await updateMileageSetting({
          variables: {
            input,
          },
        });
        refetchMileageData();
        toast.success(t("General settings updated successfully"));
      } catch (error) {
        toast.error(t("Error updating general settings", error));
      }
    },
  });

  useEffect(() => {
    const updatedGeneralSettings = {
      mileageReimbursement: {
        type: mileageData?.getMileageReimbursement?.type || "",
        rate: mileageData?.getMileageReimbursement?.rate || null,
      },
    };

    if (mileageData?.getMileageReimbursement?.type) {
      formikGeneralSettings.resetForm({
        values: {
          ...updatedGeneralSettings,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mileageData]);

  /////////////////////////////////////////
  //                                     //
  //            Loading States           //
  //                                     //
  /////////////////////////////////////////

  interface SpecificLoadingStates {
    [key: string]: boolean;
    profile: boolean;
    invoice: boolean;
    general: boolean;
    branding: boolean;
    api: boolean;
  }

  const specificLoadingStates: SpecificLoadingStates = {
    profile: loadingUser || loadingCompanyInfo,
    invoice: loadingInvoiceSettingsData,
    general: loadingMileage || loadingDefaultMileage,
    branding: loadingCustomColors,
    api: false,
  };

  const specificUploadingStates: SpecificLoadingStates = {
    profile: updatingCompany || updatingInvoiceSettings,
    invoice: updatingInvoiceSettings,
    general: updatingMileage,
    branding: isUploadingAttachments || updatingCustomColors,
    api: false,
  };

  return {
    companyProfile: formikCompanyProfile,
    paymentEmailDomain,
    generalSettings: formikGeneralSettings,
    invoiceSettings: formikCompanyInvoiceSettings,
    defaultMileageData,
    logos,
    customColors,
    handleUpdateCustomColors,
    refetchUser,
    refetchCompanyInfo,
    handleSubmitCompanyProfile: formikCompanyProfile.handleSubmit,
    handleSubmitGeneralSettings: formikGeneralSettings.handleSubmit,
    handleSubmitInvoiceSettings: formikCompanyInvoiceSettings.handleSubmit,
    uploadLogo,
    handleRemoveLogo,
    specificLoadingStates,
    specificUploadingStates,
  };
};

export default useCompanySettings;
